import { Fragment, type ReactElement } from 'react';
import {
  knownAppearance, type Accessory, type Eyes as EyeStyle, type Head, type Palette, type PersonaAppearance,
} from '@contracts/persona';
import type { Emotion } from './emotion';

/**
 * Hana's face, ported one to one from the persona mockup (`docs/specs/assets/
 * 2026-09-28-ai-assistant-persona-mockup.html`, `E`, `eyes`, `brow`, `mouth`, `extra`, `svg`). The only departures
 * are D89 (the waving hand and the hearts are SVG paths, not emoji glyphs) and the CSP: no inline `style`, so the
 * LED's glow reads `currentColor` from a class and the animation delays are classes (`hana-d-*`).
 *
 * S8 §9: the figure takes a person's `appearance` (palette, eyes, head, accessory), drawn like the S8 persona mockup
 * (`docs/specs/done/assets/2026-10-02-persona-s8-mockup.html`, Màn 3 option rows and Màn 4). Its one addition is the body
 * edge (D94): a 1.5-unit outline whose colour is a CSS class (`hana.css`), so the body stands 3:1 off the card.
 */

export type Look = {
  n: string; t: string;
  a: 'float' | 'bounce' | 'jump' | 'shake' | 'tilt' | 'lean' | 'droop' | 'pulse' | 'sleep';
  eye: 'open' | 'wide' | 'up' | 'narrow' | 'happy' | 'closed' | 'sad' | 'wink' | 'mixed';
  mouth: 'smile' | 'big' | 'o' | 'o-small' | 'flat' | 'frown' | 'wavy' | 'side' | 'smirk';
  led: string;
  x?: 'wave' | 'dots' | 'gear' | 'q' | 'q2' | 'spark' | 'bang' | 'sweat' | 'zzz' | 'heart';
  brow?: 'focus' | 'up' | 'high' | 'worry' | 'one';
  look?: readonly [number, number];
  blush?: 1;
};

/** The mockup's `E`, value for value (the page and the tests read `n`). */
export const LOOKS: Record<Emotion, Look> = {
  idle: { n: 'Bình thường', t: 'Rảnh, lơ lửng nhẹ, mắt theo con trỏ', a: 'float', eye: 'open', mouth: 'smile',
    led: '#7ce0ff' },
  greet: { n: 'Vui chào', t: 'Mở panel, chào buổi sáng', a: 'bounce', eye: 'happy', mouth: 'big', led: '#7dffa8',
    x: 'wave' },
  listen: { n: 'Lắng nghe', t: 'Anh đang gõ hoặc nói', a: 'lean', eye: 'wide', mouth: 'o-small', led: '#7ce0ff',
    look: [4, 3] },
  think: { n: 'Suy nghĩ', t: 'LLM đang suy luận', a: 'tilt', eye: 'up', mouth: 'flat', led: '#b9a7ff', x: 'dots' },
  work: { n: 'Tập trung làm', t: 'Tool đang chạy: tạo task, truy vấn', a: 'pulse', eye: 'narrow', mouth: 'flat',
    led: '#ffd166', x: 'gear', brow: 'focus' },
  ask: { n: 'Xin xác nhận', t: 'Việc nặng cần anh bấm đồng ý', a: 'tilt', eye: 'open', mouth: 'smile', led: '#ffd166',
    x: 'q', brow: 'up', look: [5, 4] },
  proud: { n: 'Tự hào', t: 'Hoàn thành, kèm lấp lánh', a: 'jump', eye: 'happy', mouth: 'big', led: '#7dffa8',
    x: 'spark' },
  surprise: { n: 'Ngạc nhiên', t: 'Phát hiện bất thường (chi tiêu tăng vọt)', a: 'jump', eye: 'wide', mouth: 'o',
    led: '#ff9f5a', x: 'bang', brow: 'high' },
  worried: { n: 'Lo lắng', t: 'Việc trễ hạn, rủi ro chiến lược', a: 'shake', eye: 'open', mouth: 'wavy',
    led: '#ff9f5a', brow: 'worry', x: 'sweat' },
  confused: { n: 'Bối rối', t: 'Lệnh mơ hồ, cần hỏi lại ("An nào?")', a: 'tilt', eye: 'mixed', mouth: 'side',
    led: '#b9a7ff', x: 'q2', brow: 'one' },
  sorry: { n: 'Xin lỗi', t: 'Lỗi, hoặc không có quyền làm việc này', a: 'droop', eye: 'sad', mouth: 'frown',
    led: '#ff6b8b', brow: 'worry' },
  sleepy: { n: 'Buồn ngủ', t: 'Lâu không dùng', a: 'sleep', eye: 'closed', mouth: 'o-small', led: '#6b7385',
    x: 'zzz' },
  wink: { n: 'Nháy mắt', t: 'Đùa nhẹ, easter egg', a: 'bounce', eye: 'wink', mouth: 'smirk', led: '#ff8ad8' },
  love: { n: 'Cảm ơn', t: 'Anh khen hoặc cảm ơn', a: 'pulse', eye: 'happy', mouth: 'big', led: '#ff8ad8', x: 'heart',
    blush: 1 },
};

/**
 * Each palette's gradient, light → mid → dark (the S8 mockup's swatches and figures). The antenna stroke and the tuft
 * are the mid stop, the headphones' cushions the light stop; `hana.css` outlines the body in the dark stop on a light
 * card and the light stop on a dark one (D94).
 */
export const PALETTE_STOPS: Record<Palette, { light: string; mid: string; dark: string }> = {
  violet: { light: '#9b82ff', mid: '#6d4dff', dark: '#3a8fe0' },
  teal: { light: '#6fe3cf', mid: '#14a896', dark: '#0e6f8a' },
  pink: { light: '#ffa3cb', mid: '#e9559a', dark: '#a64fd6' },
  orange: { light: '#ffc27a', mid: '#f9802f', dark: '#d8483a' },
  leaf: { light: '#b4e57a', mid: '#46ad43', dark: '#1d8668' },
  sky: { light: '#93dbff', mid: '#3a9ff0', dark: '#3d58dc' },
  graphite: { light: '#a7b1c2', mid: '#5d6879', dark: '#2d3747' },
  honey: { light: '#ffd86b', mid: '#eea21f', dark: '#d4691f' },
};

const PUPIL = '#1d1640';
const WHITE = '#fff';
const DARK = '#2a1a5e';

/** A white stroke, the mockup's line style for lids, brows and mouths. */
const line = (d: string, width: string, key?: string) =>
  <path key={key} d={d} stroke={WHITE} strokeWidth={width} fill="none" strokeLinecap="round" />;

/** Two decimals at most, so derived coordinates read like the mockup's ("32.6", not "32.599999"). */
const r2 = (v: number) => Math.round(v * 100) / 100;

/**
 * S8 §9, D95: the eye's style. `round` is the S1 eye. `bean` draws round's geometry without the white: the pupil
 * becomes a dark bean (rx 5, ry 6.6 for round's 8 × 10 eye, scaled with the eye) where round's pupil sits, with a
 * larger highlight. `lash` is the round eye with two curled lashes over each open eye (the mockup's Mi cong). The
 * arcs (happy, closed, the winking eye) are the same in every style, and carry no lashes.
 */
function Eyes({ kind, look, style = 'round' }: { kind: Look['eye']; look?: Look['look']; style?: EyeStyle }) {
  const lx = look?.[0] ?? 0;
  const ly = look?.[1] ?? 0;
  // `open(cx, rx, ry, pr)`: the white, the pupil (which follows the pointer from its data-cx/cy) and the highlight.
  // The mockup's `up` rewrites `cy="47"` to 42 (its data-cy too) and `sad` every 47 to 49: `pupilCy` carries both.
  // The highlight sits 3 above the pupil wherever it is (the S8 mockup; S1 left it at 44 for `up` and `sad`).
  const open = (cx: number, rx: number, ry: number, pr: number, pupilCy = 47 + ly) => {
    if (style === 'bean') {
      return (
        <Fragment key={cx}>
          <ellipse className="pupil" data-cx={cx + lx} data-cy={pupilCy} cx={cx + lx} cy={pupilCy}
            rx={r2(5 * rx / 8)} ry={r2(6.6 * ry / 10)} fill={PUPIL} />
          <circle cx={cx + lx + 2} cy={pupilCy - 3} r={1.9} fill={WHITE} />
        </Fragment>
      );
    }
    return (
      <Fragment key={cx}>
        <ellipse cx={cx} cy={46} rx={rx} ry={ry} fill={WHITE} />
        <circle className="pupil" data-cx={cx + lx} data-cy={pupilCy} cx={cx + lx} cy={pupilCy} r={pr} fill={PUPIL} />
        <circle cx={cx + lx + 2} cy={pupilCy - 3} r={1.6} fill={WHITE} />
        {style === 'lash' && lashes(cx, 46, rx, ry)}
      </Fragment>
    );
  };
  // The mockup's lashes for the 8 × 10 eye at (37|63, 46), placed on the eye's outline for any other eye size.
  const lashes = (cx: number, cy: number, rx: number, ry: number) => {
    const side = cx < 50 ? -1 : 1;
    const at = (dx: number, dy: number) => `${r2(cx + side * dx * rx / 8)} ${r2(cy - dy * ry / 10)}`;
    return <path d={`M${at(4.4, 8.5)} l${side * 3.5} -3.5 M${at(7.6, 5.5)} l${side * 4} -2`} stroke={PUPIL}
      strokeWidth="2" strokeLinecap="round" fill="none" />;
  };
  const narrow = (cx: number) => style === 'bean'
    ? <ellipse key={cx} cx={cx} cy={47} rx={5} ry={r2(6.6 * 4.5 / 10)} fill={PUPIL} />
    : (
      <Fragment key={cx}>
        <ellipse cx={cx} cy={47} rx={8} ry={4.5} fill={WHITE} />
        <circle cx={cx} cy={47} r={3.2} fill={PUPIL} />
        {style === 'lash' && lashes(cx, 47, 8, 4.5)}
      </Fragment>
    );
  let s: ReactElement | ReactElement[];
  switch (kind) {
    case 'open': s = [open(37, 8, 10, 4.5), open(63, 8, 10, 4.5)]; break;
    case 'wide': s = [open(37, 10, 12.5, 3.6), open(63, 10, 12.5, 3.6)]; break;
    case 'up': s = [open(37, 8, 10, 4.5, 42), open(63, 8, 10, 4.5, 42)]; break;
    case 'narrow': s = [narrow(37), narrow(63)]; break;
    case 'happy': s = [line('M29 49 Q37 38 45 49', '4', 'l'), line('M55 49 Q63 38 71 49', '4', 'r')]; break;
    case 'closed': s = [line('M29 48 Q37 53 45 48', '3.5', 'l'), line('M55 48 Q63 53 71 48', '3.5', 'r')]; break;
    case 'sad': s = [open(37, 7.5, 9, 4, 49), open(63, 7.5, 9, 4, 49)]; break;
    case 'wink': s = [open(37, 8, 10, 4.5), line('M55 48 Q63 40 71 48', '4', 'r')]; break;
    case 'mixed': s = [open(37, 9, 11.5, 4), open(63, 6.5, 7, 3.5)]; break;
  }
  return <g className="eyes">{s}</g>;
}

const BROWS: Record<NonNullable<Look['brow']>, string> = {
  focus: 'M28 35 L45 39 M72 35 L55 39', up: 'M29 33 Q37 29 45 33 M55 33 Q63 29 71 33',
  high: 'M29 30 Q37 25 45 30 M55 30 Q63 25 71 30', worry: 'M29 36 L44 31 M71 36 L56 31',
  one: 'M29 35 L45 35 M55 30 Q63 26 71 31',
};

function Brow({ kind }: { kind: Look['brow'] }) {
  if (kind === undefined) return null;
  return <path d={BROWS[kind]} stroke={WHITE} strokeWidth="3" fill="none" strokeLinecap="round" opacity=".9" />;
}

function Mouth({ kind }: { kind: Look['mouth'] }) {
  switch (kind) {
    case 'smile': return line('M42 64 Q50 71 58 64', '3.2');
    case 'big': return (
      <>
        <path d="M38 62 Q50 78 62 62 Z" fill={DARK} />
        <path d="M44 70 Q50 74 56 70" fill="#ff7aa8" />
      </>
    );
    case 'o': return <ellipse cx="50" cy="67" rx="5.5" ry="7" fill={DARK} />;
    case 'o-small': return <ellipse cx="50" cy="66" rx="3.5" ry="3.8" fill={DARK} />;
    case 'flat': return <path d="M43 66 L57 66" stroke={WHITE} strokeWidth="3.2" strokeLinecap="round" />;
    case 'frown': return line('M42 70 Q50 63 58 70', '3.2');
    case 'wavy': return line('M40 67 q2.5 -3 5 0 t5 0 t5 0 t5 0', '3');
    case 'side': return line('M44 67 Q52 64 58 62', '3.2');
    case 'smirk': return line('M41 64 Q52 72 60 61', '3.2');
  }
}

/**
 * D89: a raised hand — palm, four fingers and a thumb — drawn where the mockup's 18 px 👋 sat (x −10, baseline 70).
 * The zero-width returns (`V7.5 V2.5`) draw the lines between the fingers with the outline's own stroke.
 */
const HAND = 'M3 14.5 L0.9 11.2 a1.5 1.5 0 0 1 2.5 -1.7 L4 10.6 L3 10.6 V4 a1.5 1.5 0 0 1 3 0 V7.5 V2.5 '
  + 'a1.5 1.5 0 0 1 3 0 V7.5 V3 a1.5 1.5 0 0 1 3 0 V7.5 V4.5 a1.5 1.5 0 0 1 3 0 V11 '
  + 'C15 15 12.5 17.5 9 17.5 C6 17.5 4 16.5 3 14.5 Z';
/** D89: the mockup's ♥, as a path 12 units wide. */
const HEART = 'M0 3 C0 -1 6 -1 6 3 C6 -1 12 -1 12 3 C12 7 6 10 6 12 C6 10 0 7 0 3Z';

function Extra({ kind }: { kind: Look['x'] }) {
  let s: ReactElement;
  switch (kind) {
    case undefined: return null;
    case 'dots': s = (
      <g>
        <circle className="tw" cx="84" cy="14" r="3" fill="#b9a7ff" />
        <circle className="tw tw2" cx="92" cy="7" r="3.6" fill="#b9a7ff" />
        <circle className="tw tw3" cx="101" cy="-1" r="4.2" fill="#b9a7ff" />
      </g>
    ); break;
    case 'gear': s = (
      <g className="gear">
        <circle cx="90" cy="12" r="7" fill="none" stroke="#ffd166" strokeWidth="4" strokeDasharray="3 2.5" />
        <circle cx="90" cy="12" r="2.5" fill="#ffd166" />
      </g>
    ); break;
    case 'q': s = <text x="84" y="16" fontSize="20" fontWeight="800" fill="#f0a012">?</text>; break;
    case 'q2': s = (
      <>
        <text className="rise" x="82" y="18" fontSize="18" fontWeight="800" fill="#8a6dff">?</text>
        <text className="rise hana-d-9" x="92" y="12" fontSize="13" fontWeight="800" fill="#8a6dff">?</text>
      </>
    ); break;
    case 'bang': s = <text x="84" y="16" fontSize="22" fontWeight="900" fill="#ff7a2e">!</text>; break;
    case 'spark': s = (
      <>
        <text className="tw" x="-6" y="18" fontSize="14" fill="#ffc53d">✦</text>
        <text className="tw tw2" x="88" y="10" fontSize="16" fill="#ffc53d">✦</text>
        <text className="tw tw3" x="92" y="60" fontSize="11" fill="#ffc53d">✦</text>
      </>
    ); break;
    case 'sweat': s = <path className="rise" d="M84 30 q4 7 0 10 q-4 -3 0 -10z" fill="#8fd3ff" />; break;
    case 'zzz': s = (
      <>
        <text className="rise" x="80" y="14" fontSize="13" fontWeight="800" fill="#8a92a3">z</text>
        <text className="rise hana-d-7" x="90" y="6" fontSize="16" fontWeight="800" fill="#8a92a3">Z</text>
      </>
    ); break;
    // The animated element sits inside the positioned group: a CSS transform animation replaces the element's own
    // `transform` attribute, so the placement and the motion cannot share one element.
    case 'heart': s = (
      <>
        <g transform="translate(84 4) scale(1.33)"><path className="rise" d={HEART} fill="#ff5c8a" /></g>
        <g transform="translate(-6 12)"><path className="rise hana-d-10" d={HEART} fill="#ff5c8a" /></g>
      </>
    ); break;
    case 'wave': s = (
      <g transform="translate(-11 50) scale(1.2)">
        <g className="wave">
          <path d={HAND} fill="#ffcf6b" stroke="#e0a23a" strokeWidth="1.2" strokeLinejoin="round" />
        </g>
      </g>
    ); break;
  }
  return <g className="extra">{s}</g>;
}

/** The top of the head; each keeps the mood LED (S8 §9: the antenna's tip, inside both ears, the tuft's tip). */
function HeadPart({ head, palette, gradientId, led }: { head: Head; palette: Palette; gradientId: string; led: string }) {
  const mid = PALETTE_STOPS[palette].mid;
  switch (head) {
    case 'antenna': return (
      <>
        <line x1="50" y1="14" x2="50" y2="4" stroke={mid} strokeWidth="3" strokeLinecap="round" />
        <circle className="led" cx="50" cy="3" r="4.5" fill={led} color={led} />
      </>
    );
    case 'ears': return (
      <>
        {([[26, -24], [74, 24]] as const).map(([cx, turn]) => (
          <g key={cx} transform={`rotate(${turn} ${cx} 22)`}>
            <ellipse cx={cx} cy="22" rx="11" ry="15" fill={`url(#${gradientId})`} />
            <ellipse className="led" cx={cx} cy="18" rx="5" ry="8" fill={led} color={led} opacity=".95" />
          </g>
        ))}
      </>
    );
    case 'tuft': return (
      <>
        <path d="M47 16 C42 6 50 -2 60 1 C54 3 51 8 54 16 Z" fill={mid} />
        <circle className="led" cx="60" cy="2" r="3.8" fill={led} color={led} />
      </>
    );
  }
}

const INK = '#1d1640';
const FELT = '#2f3a4c';
const BOW_FILL = '#ff5c8a';
const BOW_EDGE = '#d63c72';
/** The flower's five petals around its heart at (22, 21), the S8 mockup's coordinates. */
const PETALS: readonly (readonly [string, string])[] = [['27.2', '21.0'], ['23.6', '25.9'], ['17.8', '24.1'],
  ['17.8', '17.9'], ['23.6', '16.1']];

/** The accessory, drawn over the face after the mouth (the S8 mockup's order), so glasses sit over the eyes. */
function AccessoryPart({ accessory, palette }: { accessory: Accessory; palette: Palette }) {
  switch (accessory) {
    case 'none': return null;
    case 'glasses': return (
      <>
        <g fill="rgba(255,255,255,.10)" stroke={INK} strokeWidth="2.6">
          <circle cx="37" cy="46" r="12.5" />
          <circle cx="63" cy="46" r="12.5" />
        </g>
        <path d="M48.5 44 Q50 41.5 51.5 44 M24.5 44 L13 40 M75.5 44 L87 40" stroke={INK} strokeWidth="2.6" fill="none"
          strokeLinecap="round" />
      </>
    );
    case 'bow': return (
      <g transform="translate(76 20) rotate(20)">
        <path d="M0 0 L-12 -8 Q-14 0 -12 8 Z" fill={BOW_FILL} stroke={BOW_EDGE} strokeWidth="1.2" strokeLinejoin="round" />
        <path d="M0 0 L12 -8 Q14 0 12 8 Z" fill={BOW_FILL} stroke={BOW_EDGE} strokeWidth="1.2" strokeLinejoin="round" />
        <circle r="3.4" fill={BOW_EDGE} />
      </g>
    );
    case 'hat': return (
      <g transform="rotate(-14 46 17)">
        <ellipse cx="46" cy="18" rx="27" ry="8.5" fill={FELT} />
        <ellipse cx="42" cy="14" rx="12" ry="3" fill={WHITE} opacity=".12" />
        <circle cx="46" cy="9" r="3.2" fill={FELT} />
      </g>
    );
    case 'headphones': {
      const light = PALETTE_STOPS[palette].light;
      return (
        <>
          <path d="M13 50 Q13 9 50 9 Q87 9 87 50" stroke={FELT} strokeWidth="4.5" fill="none" strokeLinecap="round" />
          <rect x="4" y="40" width="13" height="22" rx="6" fill={FELT} />
          <rect x="83" y="40" width="13" height="22" rx="6" fill={FELT} />
          <rect x="7" y="45" width="4" height="12" rx="2" fill={light} />
          <rect x="89" y="45" width="4" height="12" rx="2" fill={light} />
        </>
      );
    }
    case 'flower': return (
      <>
        {PETALS.map(([cx, cy]) => (
          <circle key={`${cx},${cy}`} cx={cx} cy={cy} r="4.4" fill="#fff6fb" stroke="#ffb3d1" strokeWidth="1" />
        ))}
        <circle cx="22" cy="21" r="3.6" fill="#ffc53d" />
      </>
    );
  }
}

/**
 * The mockup's `svg()` body, in the given appearance (D93: no default). `gradientId` must be
 * unique on the page (two figures can show at once).
 */
export function HanaSvg({ emotion, size, gradientId, appearance: asGiven }: {
  emotion: Emotion; size: number; gradientId: string; appearance: PersonaAppearance;
}) {
  // D111: a key from another release draws its axis's default part, never a crash outside every error boundary.
  const appearance = knownAppearance(asGiven);
  const look = LOOKS[emotion];
  const cheek = look.blush ? '.75' : '.35';
  const stops = PALETTE_STOPS[appearance.palette];
  return (
    <svg viewBox="0 0 100 100" width={size} height={size}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={stops.light} />
          <stop offset=".55" stopColor={stops.mid} />
          <stop offset="1" stopColor={stops.dark} />
        </linearGradient>
      </defs>
      <HeadPart head={appearance.head} palette={appearance.palette} gradientId={gradientId} led={look.led} />
      {/* A round head, not the mockup's squircle (rx 30): same box, the highlight moved inside the curve. */}
      <ellipse cx="50" cy="51" rx="38" ry="37" fill={`url(#${gradientId})`} />
      {/* D94: the body's outline, coloured by `hana.css` (`.edge.pal-*`, light and `.dark`); no inline style (CSP). */}
      <ellipse className={`edge pal-${appearance.palette}`} cx="50" cy="51" rx="38" ry="37" fill="none"
        strokeWidth="1.5" />
      <ellipse cx="38" cy="25" rx="12" ry="4.5" fill={WHITE} opacity=".18" />
      <ellipse cx="26" cy="60" rx="6" ry="3.5" fill="#ff8ab8" opacity={cheek} />
      <ellipse cx="74" cy="60" rx="6" ry="3.5" fill="#ff8ab8" opacity={cheek} />
      <Brow kind={look.brow} />
      <Eyes kind={look.eye} look={look.look} style={appearance.eyes} />
      <Mouth kind={look.mouth} />
      <AccessoryPart accessory={appearance.accessory} palette={appearance.palette} />
      <Extra kind={look.x} />
    </svg>
  );
}
